/**
 * FarmOverviewCard
 *
 * Displays a single farm's irrigation overview: a map preview, device stats
 * (active valves, tank levels, weather), and action buttons.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Droplets,
  Thermometer,
  Wind,
  CloudRain,
  Wifi,
  WifiOff,
  Settings,
  ChevronRight,
  Map as MapIcon,
} from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import FarmOverviewMap from './FarmOverviewMap';
import ControllerStopControl from './ControllerStopControl';
import type { OverviewFarm } from '../../services/irrigationOverview';
import { isRelayOn } from '../../services/irrigationOverview';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { formatNum, toNum, toNumOr } from '@/utils/numeric';

interface FarmOverviewCardProps {
  farm: OverviewFarm;
  /** Re-read the overview after a stop or resume changed something. */
  onChanged?: () => void;
}

export default function FarmOverviewCard({ farm, onChanged }: FarmOverviewCardProps) {
  const navigate = useNavigate();
  // Licensed feature, not a role: the Setup link lands on a page gated by the
  // irrigation admin seat.
  const { hasAccess: canManageDevices } = useModuleLicense('irrigation', 'admin');

  // Compute summary stats
  const stats = useMemo(() => {
    let totalRelays = 0;
    let activeRelays = 0;
    let onlineRouters = 0;
    let totalRouters = 0;

    for (const c of farm.controllers) {
      for (const r of c.routers) {
        totalRouters++;
        if (r.online) onlineRouters++;
        // Count relays that have zones (configured relays)
        const routerZones = c.zones.filter((z) => z.router_mac === r.router_mac);
        for (const z of routerZones) {
          totalRelays++;
          if (isRelayOn(r.relay_states, z.relay_index)) activeRelays++;
        }
      }
    }

    // Tank sensors
    const tankSensors = farm.sensors.filter((s) => s.type_key === 'tank_level');
    const lowestTank =
      tankSensors.length > 0
        ? tankSensors.reduce((min, s) =>
            s.latest_value != null && (min == null || s.latest_value < min)
              ? s.latest_value
              : min,
          null as number | null)
        : null;

    // Weather
    const wx = farm.weather_stations[0]?.latest_observation ?? null;

    return { totalRelays, activeRelays, onlineRouters, totalRouters, tankSensors, lowestTank, wx };
  }, [farm]);

  // The farm's own live page — map, what is running, what is scheduled. It
  // used to be the controller's device screen, which answers a question only
  // whoever installed the hardware is asking; devices are still one click on
  // from there.
  const handleViewFarm = () => {
    navigate(`/irrigation/farms/${farm.id}`);
  };

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 pt-5 pb-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">{farm.name}</h3>
          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
            {stats.onlineRouters > 0 ? (
              <Wifi className="w-3 h-3 text-green-500" />
            ) : stats.totalRouters > 0 ? (
              <WifiOff className="w-3 h-3 text-gray-400" />
            ) : null}
            {stats.totalRouters > 0 && `${stats.onlineRouters}/${stats.totalRouters}`}
            {farm.sensors.length > 0 &&
              `${stats.totalRouters > 0 ? ' | ' : ''}${farm.sensors.length} sensor${farm.sensors.length !== 1 ? 's' : ''}`}
            {farm.weather_stations.length > 0 &&
              `${stats.totalRouters > 0 || farm.sensors.length > 0 ? ' | ' : ''}${farm.weather_stations.length} wx`}
          </span>
        </div>
      </div>

      {/* Map */}
      <div className="px-4 pb-2">
        <FarmOverviewMap
          boundaryGeometry={farm.boundary_geometry}
          centerPoint={farm.center_point}
          controllers={farm.controllers}
          sensors={farm.sensors}
          weatherStations={farm.weather_stations}
          onClick={handleViewFarm}
        />
      </div>

      {/* Stats row */}
      <CardContent className="pt-3 pb-4 px-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          {/* Active valves */}
          {stats.totalRelays > 0 && (
            <div className="flex items-center gap-1.5">
              <Droplets
                className={`w-4 h-4 ${stats.activeRelays > 0 ? 'text-blue-500' : 'text-gray-400'}`}
              />
              <span>
                <span className={stats.activeRelays > 0 ? 'font-semibold text-blue-600' : ''}>
                  {stats.activeRelays}
                </span>
                /{stats.totalRelays} valves
              </span>
            </div>
          )}

          {/* Lowest tank */}
          {stats.lowestTank != null && (
            <div className="flex items-center gap-1.5">
              <Droplets className={`w-4 h-4 ${stats.lowestTank < 25 ? 'text-red-500' : 'text-cyan-500'}`} />
              <span>
                Tank{' '}
                <span className={stats.lowestTank < 25 ? 'font-semibold text-red-600' : ''}>
                  {stats.lowestTank.toFixed(0)}%
                </span>
              </span>
            </div>
          )}

          {/* Weather */}
          {stats.wx && (
            <>
              {toNum(stats.wx.temp_c) != null && (
                <div className="flex items-center gap-1">
                  <Thermometer className="w-4 h-4 text-orange-500" />
                  <span>{formatNum(stats.wx.temp_c, 1)}°C</span>
                </div>
              )}
              {toNum(stats.wx.wind_kph) != null && (
                <div className="flex items-center gap-1">
                  <Wind className="w-4 h-4 text-gray-500" />
                  <span>{formatNum(stats.wx.wind_kph, 0)} km/h</span>
                </div>
              )}
              {toNumOr(stats.wx.rain_mm_day) > 0 && (
                <div className="flex items-center gap-1">
                  <CloudRain className="w-4 h-4 text-blue-500" />
                  <span>{formatNum(stats.wx.rain_mm_day, 1)} mm</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Stop watering — one control per site on this farm.
            Above the navigation buttons on purpose: it is the only thing here
            that acts rather than navigates, and the moment somebody wants it is
            the moment they are looking at this card watching a farm water
            something it should not be. A held site replaces its own button with
            the banner explaining the hold, so a stopped farm cannot be read as a
            quiet one. */}
        {farm.controllers.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            {farm.controllers.map((c) => (
              <ControllerStopControl
                key={c.controller_id}
                controller={c}
                showName={farm.controllers.length > 1}
                onChanged={onChanged}
              />
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button size="sm" onClick={handleViewFarm}>
            View Farm <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/irrigation/network?farm=${farm.id}`)}
          >
            <MapIcon className="w-4 h-4 mr-1" /> Network
          </Button>
          {canManageDevices && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/irrigation/espnow/admin')}
            >
              <Settings className="w-4 h-4 mr-1" /> Setup
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
